import { useRef, useState, type ComponentType } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import { TrayIcon } from "@/components/icons";
import { ReportsIcon, TimeIcon, TodayIcon } from "@/components/NavIcons";
import { hapticTick } from "@/lib/haptics";

/**
 * Four destinations, equal width.
 *
 * There was a raised + in the middle for logging, but Time does everything the
 * sheet behind it did — including picking a date — so the button was a second
 * door into the same room. Four tabs at ~94pt each are well past the 44pt
 * minimum, and the thumb no longer has to avoid a target it didn't want.
 *
 * Tasks keeps the /projects route (old links and bookmarks still land) but
 * takes the desktop sidebar's name and calendar-check icon, so the same place
 * is called the same thing on both.
 */
const TABS = [
  { to: "/", label: "Time", Icon: TimeIcon, end: true },
  { to: "/projects", label: "Tasks", Icon: TodayIcon, end: false },
  { to: "/entries", label: "Entries", Icon: TrayIcon, end: false },
  { to: "/insights", label: "Insights", Icon: ReportsIcon, end: false },
];

const PAD = 5;

function activeIndex(pathname: string): number {
  return TABS.findIndex((t) => (t.end ? pathname === t.to || pathname === "/time" : pathname.startsWith(t.to)));
}

/**
 * A floating bar, as on current iOS: a rounded, frosted capsule held just
 * above the bottom edge, with the page scrolling underneath it. It is fixed
 * rather than in the column, so content runs beneath; every phone screen
 * already pads its bottom by --above-tabbar, which counts the bar, its offset
 * and the gap above it, so nothing ends up hidden behind it.
 *
 * It moves like the iOS 26 bar: the selected capsule slides to the tab you
 * touch and swells slightly while it travels, and you can keep your finger
 * down and scrub along the bar, the capsule following, to land on any tab.
 * Each tab it crosses gives a haptic tick (lib/haptics). Links underneath stay
 * real links, so keyboard and screen-reader use is unchanged.
 */
export function MobileTabBar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const nav = useRef<HTMLElement>(null);
  const current = activeIndex(pathname);

  /** Where the finger is, while it is down: the tab under it and the x it's at. */
  const [press, setPress] = useState<{ index: number; x: number; moved: boolean } | null>(null);
  const ticked = useRef(-1);
  // The pointer sequence already navigated; the click that follows must not.
  const handled = useRef(false);

  function slot(): { left: number; width: number } {
    const r = nav.current!.getBoundingClientRect();
    return { left: r.left + PAD, width: (r.width - PAD * 2) / TABS.length };
  }
  function indexAt(clientX: number): number {
    const { left, width } = slot();
    return Math.max(0, Math.min(TABS.length - 1, Math.floor((clientX - left) / width)));
  }

  function down(e: React.PointerEvent<HTMLElement>) {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    // Keeps the drag ours when the finger slides off the bar. It throws for a
    // pointer the browser no longer considers active; the bar works without it.
    try { nav.current?.setPointerCapture(e.pointerId); } catch { /* fine */ }
    const index = indexAt(e.clientX);
    ticked.current = current;
    setPress({ index, x: e.clientX, moved: false });
  }

  function move(e: React.PointerEvent<HTMLElement>) {
    if (!press) return;
    const index = indexAt(e.clientX);
    if (index !== ticked.current) { ticked.current = index; hapticTick(); }
    setPress({ index, x: e.clientX, moved: true });
  }

  function up(e: React.PointerEvent<HTMLElement>) {
    if (!press) return;
    const index = indexAt(e.clientX);
    setPress(null);
    handled.current = true;
    // With the pointer captured, the click may land on the bar rather than a
    // link — so the flag can't wait for a click to clear it.
    setTimeout(() => { handled.current = false; }, 0);
    if (index !== ticked.current) hapticTick();
    if (index !== current) navigate(TABS[index].to);
  }

  // The capsule's position: under the finger while pressed, else on the
  // tab. At rest it is placed in its own widths (100% = one tab), so it needs
  // no measuring and is right on the very first paint.
  let capsule: { x: string; scale: number; fast: boolean } | null = null;
  if (press && nav.current) {
    const { left, width } = slot();
    // Follows the finger, but never past either end of the bar.
    const x = Math.max(0, Math.min(width * (TABS.length - 1), press.x - left - width / 2));
    capsule = { x: `${x}px`, scale: 1.12, fast: press.moved };
  } else if (current >= 0) {
    capsule = { x: `${current * 100}%`, scale: 1, fast: false };
  }
  const lit = press ? press.index : current;

  return (
    <nav
      ref={nav}
      className="no-print mobile-tabbar"
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={() => setPress(null)}
      style={{
        position: "fixed", zIndex: 40,
        left: 12, right: 12, bottom: "var(--tabbar-offset)",
        height: "var(--tabbar-h)",
        display: "flex", alignItems: "stretch", padding: PAD,
        borderRadius: 999,
        border: "1px solid var(--line)",
        boxShadow: "0 10px 30px rgba(20,22,26,0.16), 0 2px 6px rgba(20,22,26,0.08)",
        // The bar owns horizontal drags; nothing behind it should scroll.
        touchAction: "none",
      }}
    >
      {capsule && (
        <span
          aria-hidden
          className="tab-lens"
          style={{
            position: "absolute", top: PAD, bottom: PAD, left: PAD,
            width: `calc((100% - ${PAD * 2}px) / ${TABS.length})`,
            borderRadius: 999, background: "var(--accentTint)",
            transform: `translateX(${capsule.x}) scale(${capsule.scale})`,
            // Springy on a jump, nearly instant while it tracks a finger.
            transition: capsule.fast
              ? "transform 60ms linear"
              : "transform 320ms cubic-bezier(.3,1.35,.5,1)",
            pointerEvents: "none",
          }}
        />
      )}
      {TABS.map((t, i) => (
        <Tab key={t.to} {...t} lit={i === lit} pressed={!!press && press.index === i}
          onClick={(e) => { if (handled.current) { e.preventDefault(); handled.current = false; } }} />
      ))}
    </nav>
  );
}

function Tab({ to, label, Icon, end, lit, pressed, onClick }: {
  to: string; label: string; Icon: ComponentType<{ size?: number }>; end: boolean;
  lit: boolean; pressed: boolean; onClick: (e: React.MouseEvent) => void;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      draggable={false}
      className="tab-lens"
      style={{
        flex: 1, position: "relative",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        gap: 1, padding: 0,
        textDecoration: "none", fontSize: 11.5, lineHeight: "14px", letterSpacing: 0,
        // --ink2 rather than --muted: at 10px/400 the inactive tabs read as
        // disabled. The lit tab wins on colour, weight and the capsule behind.
        color: lit ? "var(--accent)" : "var(--ink2)",
        fontWeight: lit ? 700 : 600,
        // The icon and label under the finger swell with the capsule.
        transform: pressed ? "scale(1.1)" : "scale(1)",
        transition: "transform 220ms cubic-bezier(.3,1.35,.5,1), color 120ms ease",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", height: 26 }}>
        <Icon size={22} />
      </span>
      {label}
    </NavLink>
  );
}
